import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Paper from '@mui/material/Paper'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { LicensingBill, PaymentStatusKey } from '../../../swagger'
import Header from '../../Elements/Header'
import BillingHistorySummaryTable, {
  BillingHistorySummaryTableVariant,
} from './BillingHistorySummaryTable'
import { IlicensingProgramOptionsWithOwedValue } from '../../Admin/UserAccounts/UserAccount'
import ProgramLicensingInformation from './ProgramLicensingInformation'
import { dateToSlashStringReinterpretedAsLocal } from '../../../utils/dateUtility'
import IconTextButton, {
  IconTextButtonVariant,
} from '../../Buttons/IconTextButton'
import PrintIcon from '@mui/icons-material/Print'
import SpaceBetweenSection from '../../Elements/SpaceBetweenSection'
import {
  SnackbarSeverity,
  useSnackbarContext,
} from '../../Context/SnackbarContext'
import { extractedErrorObject, paymentsApi } from '../../../api/swagger'
import { useAuth } from '../../Routes/AuthProvider'
import { Divider } from '@mui/material'
import { Payment } from '../../Interfaces/Payment'
import { useMountEffect } from '../../../hooks/useMountEffect'
export interface LicensingPaymentsCardProps {
  licensingPaymentHistory: Payment[]
  licensingBills: LicensingBill[]
  achDiscount?: number
  amountDue?: number
  userKey?: number
  variantForBillingHistorySummaryTable?: BillingHistorySummaryTableVariant
  refetch?: () => void
  hasPermissionToFetchPaymentOptions?: boolean
  licensingProgramOptions?: IlicensingProgramOptionsWithOwedValue[]
  achAllowed: boolean
}

const ACADEMIC_YEAR_TO_DISABLE_SHOW_LICENSING_DETAILS = 2022
export const LicensingPaymentsCard: React.FC<LicensingPaymentsCardProps> = ({
  licensingPaymentHistory,
  licensingBills,
  achDiscount,
  amountDue,
  userKey,
  variantForBillingHistorySummaryTable,
  refetch,
  hasPermissionToFetchPaymentOptions,
  licensingProgramOptions,
  achAllowed,
}) => {
  const { t } = useTranslation()
  const { setSnackbarMessage, setSnackbarSeverity, setSnackbarState } =
    useSnackbarContext()
  const { permissionAbility, userDetails } = useAuth()
  // used to condition print reports button render based on actor permissions
  const canViewProgramFinancialReport = permissionAbility.can(
    'canViewProgramFinancialReport',
    'Reports'
  )
  const actorKey = userDetails.actingAs

  const isLicensingPaymentsCSR =
    !!variantForBillingHistorySummaryTable &&
    variantForBillingHistorySummaryTable ===
      BillingHistorySummaryTableVariant.LicensingPaymentsCSR

  const disableMakeAPayment =
    !!licensingPaymentHistory &&
    licensingPaymentHistory.some((payment) => {
      return payment.paymentStatusKey === PaymentStatusKey.Err
    })

  const cannotMakePaymentError = t(
    'Account.BillingTab.CannotMakePayments.ErrorMessage',
    'An error has been identified with your transaction. Kindly reach out to our Customer Support Representative for assistance.'
  )

  useMountEffect(() => {
    if (disableMakeAPayment) {
      setSnackbarSeverity(SnackbarSeverity.Error)
      setSnackbarMessage(cannotMakePaymentError)
      setSnackbarState(true)
    }
  })

  const disableShowDetails = (licensingBill: LicensingBill) => {
    return (
      licensingBill._for.semesterOneStartDate &&
      licensingBill._for.semesterOneStartDate.getFullYear() <=
        ACADEMIC_YEAR_TO_DISABLE_SHOW_LICENSING_DETAILS
    )
  }

  const errorMessageCreatingReports = t(
    'Account.BillingTab.PrintReports.ErrorMessage',
    'Something went wrong while loading billing data. Please reload the page.'
  )

  const printReports = async () => {
    try {
      const reportsUrl = process.env.REACT_APP_REPORTS_URL ?? ''
      const eventReportUrl = process.env.REACT_APP_LICENSING_REPORT_URL ?? ''
      const encodedURL = encodeURIComponent(`${eventReportUrl}&ak=${actorKey}`)
      const { href } = new URL(
        `${reportsUrl}?ReturnURL=${encodedURL}`,
        undefined
      )
      await paymentsApi.fetchBilling({ generateReport: true })
      // if the response is not a 200 we will go into catch block otherwise we allow the href to open a new window
      window.open(href, '_blank')
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'Unknown Code',
        message: (e as unknown as Error).message ?? errorMessageCreatingReports,
      }
      setSnackbarSeverity?.(SnackbarSeverity.Error)
      setSnackbarMessage?.(errorObject.message)
      setSnackbarState?.(true)
    }
  }

  return (
    <Card aria-labelledby="licensingPayments" component={Paper}>
      {!isLicensingPaymentsCSR && (
        <SpaceBetweenSection
          isCentered
          marginRight={5}
          marginLeft={5}
          left={
            <Header
              id="licensingPayments"
              headerName={t(
                'LicensingPaymentsCard.Heading.LicensingPayments',
                'Licensing Payments'
              )}
              component="h6"
            />
          }
          right={
            canViewProgramFinancialReport ? (
              <IconTextButton
                id="printReports"
                variant={IconTextButtonVariant.LicensingFeeReport}
                startIcon={<PrintIcon />}
                onClick={printReports}
              />
            ) : null
          }
        ></SpaceBetweenSection>
      )}
      {!!licensingBills &&
        licensingBills.length > 0 &&
        licensingBills.map((licensingBill) => {
          return (
            <Box mx={3} mt={3} key={licensingBill._for.programKey}>
              <ProgramLicensingInformation
                header={
                  `${licensingBill._for.programType ?? ''} (${
                    licensingBill._for.semesterOneStartDate
                      ? dateToSlashStringReinterpretedAsLocal(
                          licensingBill._for.semesterOneStartDate as Date
                        )
                      : ''
                  }) - ${licensingBill._for.communityName ?? ''}` ?? ''
                }
                program={licensingBill}
                achDiscount={achDiscount ?? 0}
                refetch={refetch}
                disableMakeAPayment={disableMakeAPayment}
                disableShowDetails={disableShowDetails(licensingBill)}
                variant={variantForBillingHistorySummaryTable}
                achAllowed={achAllowed}
              />
              <Divider sx={{ borderBottomWidth: '5px' }} />
            </Box>
          )
        })}
      <BillingHistorySummaryTable
        variant={
          variantForBillingHistorySummaryTable ??
          BillingHistorySummaryTableVariant.LicensingPayments
        }
        ariaLabelledBy="licensingPaymentHistory"
        paymentHistory={licensingPaymentHistory}
        amountDue={amountDue ?? 0}
        userKey={userKey}
        triggerRefresh={refetch}
        hasPermissionToFetchPaymentOptions={hasPermissionToFetchPaymentOptions}
        licensingProgramOptions={licensingProgramOptions}
      />
    </Card>
  )
}

export default LicensingPaymentsCard
